import React from 'react';
import { X } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';

const content = {
  en: {
    title: "Terms of Service", 
    text: "Your trust is our priority. By engaging with the Ahlafot platform, you join a community built on mutual respect and fair exchange. Our terms are designed to ensure a safe, transparent, and legal environment for all users.\n\nWe facilitate connections, but the integrity of each trade rests with our community. We require all users to be of legal age and strictly prohibit the exchange of regulated or illegal goods. For a detailed breakdown of our user agreements and dispute resolution processes, please review the full document below." 
  },
  he: {
    title: "תנאי שימוש", 
    text: "האמון שלכם הוא בראש סדר העדיפויות שלנו. על ידי שימוש בפלטפורמת Ahlafot, אתם מצטרפים לקהילה שנבנתה על כבוד הדדי והחלפה הוגנת. התנאים שלנו נועדו להבטיח סביבה בטובה, שקופה וחוקית לכל המשתמשים.\n\nאנו מקשרים בין אנשים, אך היושרה של כל עסקה נשענת על הקהילה שלנו. אנו דורשים מכל המשתמשים להיות בגיל החוקי ואוסרים בהחלט על החלפת סחורות מפוקחות או לא חוקיות. לפירוט מלא של הסכמי המשתמש ותהליכי יישוב המחלוקות שלנו, אנא עיינו במסמך המלא להלן." 
  }
};

export default function TermsModal({ isOpen, onClose }) {
  const { language, t } = useLanguage();
  const pageData = content[language] || content.en;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="bg-card w-full max-w-2xl max-h-[80vh] rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col border border-border"
        >
          <div className="p-6 border-b border-border flex justify-between items-center bg-muted/20">
            <h2 className="text-xl font-black uppercase tracking-tighter">{pageData.title}</h2>
            <button onClick={onClose} className="p-2 hover:bg-muted rounded-full transition-colors">
              <X size={24} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-8 md:p-12">
            <div className="prose dark:prose-invert max-w-none">
                <p className="text-lg text-muted-foreground leading-relaxed whitespace-pre-wrap">
                    {pageData.text}
                </p>
                <div className="mt-8 p-6 bg-primary/5 rounded-2xl border border-primary/10">
                    <p className="text-sm font-bold text-primary italic">
                        {language === 'he' ? '* מסמך זה הוא תקציר של תנאי השימוש המלאים.' : '* This document is a summary of the full Terms of Service.'}
                    </p>
                </div>
            </div>
          </div>
          <div className="p-6 border-t border-border bg-muted/20 flex justify-center">
            <button 
              onClick={onClose}
              className="px-12 py-3 bg-primary text-white rounded-2xl font-black uppercase tracking-widest text-xs hover:scale-105 active:scale-95 transition-all shadow-lg shadow-primary/20"
            >
              {t('close')}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
